<template>
    <div v-show="localVisible" class="dialog-mask" @click.self="handleMaskClick" :style="{ zIndex }" :id="'draggable-dialog-' + zIndex">
        <div class="dialog-container" :class="{ fullscreen: isFullscreen, 'is-draggable': draggable && !isFullscreen }" :style="dialogStyle" ref="dialog"
            @mousedown.self="bringToFront">
            <!-- 头部 -->
            <div class="dialog-header" @mousedown.stop="onHeaderMouseDown"
                :style="headerStyle">
                <span class="dialog-title">{{ title }}</span>
                <div class="dialog-controls">
                    <button class="icon-btn" @click.stop="toggleFullscreen" title="全屏" v-if="fullscreen">
                        <span class="icon">🗖</span>
                    </button>
                    <button class="icon-btn close" @click.stop="handleClose" title="关闭">
                        <span class="icon">✖</span>
                    </button>
                </div>
            </div>

            <!-- 内容 -->
            <div class="dialog-body">
                <slot></slot>
            </div>

            <!-- 底部 -->
            <div class="dialog-footer" v-if="$slots.footer">
                <slot name="footer"></slot>
            </div>
        </div>
    </div>
</template>

<script>
import { ensureElementPopupAboveDialogs } from '@/script/dialogZIndex.js';

let zIndexSeed = 10000;

export default {
    name: 'DraggableDialog',
    props: {
        title: {
            type: String,
            default: '设置面板'
        },
        visible: {
            type: Boolean,
            default: false
        },
        width: {
            type: Number,
            default: 1200
        },
        height: {
            type: Number,
            default: 800
        },
        draggable: {
            type: Boolean,
            default: true
        },
        closeOnEsc: {
            type: Boolean,
            default: true
        },
        closeOnClickMask: {
            type: Boolean,
            default: true
        },
        fullscreen: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            localVisible: this.visible,
            isFullscreen: false,
            pos: { x: 0, y: 0 },
            dragging: false,
            offset: { x: 0, y: 0 },
            zIndex: zIndexSeed++
        };
    },
    computed: {
        dialogStyle() {
            if (this.isFullscreen) {
                return { top: '0', left: '0', width: '100vw', height: '100vh' };
            } else {
                return {
                    top: this.pos.y + 'px',
                    left: this.pos.x + 'px',
                    width: this.width + 'px',
                    height: this.height + 'px'
                };
            }
        },
        headerStyle() {
            return this.$attrs.headerstyle || {};
        }
    },
    watch: {
        visible(val) {
            this.localVisible = val;
            if (val) {
                this.centerDialog();
                this.zIndex = zIndexSeed++;
                this.$nextTick(() => ensureElementPopupAboveDialogs());
            }
        },
        localVisible(val) {
            this.$emit('update:visible', val);
        }
    },
    mounted() {
        if (this.localVisible) {
            this.centerDialog();
        }
        window.addEventListener('mousemove', this.onDrag);
        window.addEventListener('mouseup', this.stopDrag);
    },
    beforeDestroy() {
        window.removeEventListener('mousemove', this.onDrag);
        window.removeEventListener('mouseup', this.stopDrag);
    },
    methods: {
        bringToFront() {
            this.zIndex = zIndexSeed++;
            ensureElementPopupAboveDialogs();
        },
        centerDialog() {
            const w = window.innerWidth;
            const h = window.innerHeight;
            this.pos.x = Math.max(0, Math.floor((w - this.width) / 2));
            this.pos.y = Math.max(0, Math.floor((h - this.height) / 2));
        },
        onHeaderMouseDown(e) {
            if (e.target && e.target.closest && e.target.closest('.dialog-controls')) {
                return;
            }
            if (this.draggable && !this.isFullscreen) {
                this.startDrag(e);
            }
        },
        startDrag(e) {
            if (!this.draggable || this.isFullscreen) return;
            this.dragging = true;
            this.bringToFront();
            this.offset.x = e.clientX - this.pos.x;
            this.offset.y = e.clientY - this.pos.y;
        },
        onDrag(e) {
            if (!this.dragging || this.isFullscreen) return;

            let newX = e.clientX - this.offset.x;
            let newY = e.clientY - this.offset.y;

            const maxX = window.innerWidth - this.width;
            const maxY = window.innerHeight - this.height;

            // 边界限制
            newX = Math.max(0, Math.min(newX, maxX));
            newY = Math.max(0, Math.min(newY, maxY));

            // 吸附边缘
            const threshold = 20;
            if (newX < threshold) newX = 0;
            if (newY < threshold) newY = 0;
            if (maxX - newX < threshold) newX = maxX;
            if (maxY - newY < threshold) newY = maxY;

            this.pos.x = newX;
            this.pos.y = newY;
        },
        stopDrag() {
            this.dragging = false;
        },
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },
        handleClose() {
            this.localVisible = false;
            this.$emit('close');
        },
        handleKeydown(e) {
            if (e.key === 'Escape' && this.localVisible && this.closeOnEsc) {
                this.handleClose();
            } else if (e.key === 'Enter' && this.$refs.dialog && this.$refs.dialog.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
        },
        handleMaskClick() {
            if (this.closeOnClickMask) {
                this.handleClose();
            }
        }
    }
};
</script>

<style scoped>
.dialog-mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.35);
    pointer-events: auto;
    -webkit-app-region: no-drag;
}

.dialog-container {
    position: absolute;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 0 10px #aaa;
    display: flex;
    flex-direction: column;
    border: 1px solid #ccc;
    transition: box-shadow 0.2s;
    z-index: 1;
    pointer-events: auto;
    cursor: default;
    /* Electron 窗口拖拽区域：弹窗内容必须标记为 no-drag 才能正常点击输入 */
    -webkit-app-region: no-drag;
}

.dialog-container.is-draggable .dialog-header {
    cursor: move;
}

.dialog-header {
    height: 40px;
    flex-shrink: 0;
    background: #f5f5f5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 12px;
    user-select: none;
    border-bottom: 1px solid #ddd;
    -webkit-app-region: no-drag;
}

.dialog-title {
    font-size: 12px;
}

.dialog-controls {
    display: flex;
    gap: 6px;
}

.icon-btn {
    background: transparent;
    border: none;
    border-radius: 4px;
    width: 30px;
    height: 30px;
    line-height: 30px;
    cursor: pointer;
    transition: background 0.2s;
}

.icon-btn:hover {
    background-color: #dcdcdc;
}

.icon-btn.close:hover {
    background-color: #ff4444;
    color: #FFF;
}

.icon {
    font-size: 14px;
}

.dialog-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    position: relative;
    pointer-events: auto;
    cursor: default;
    -webkit-app-region: no-drag;
}

.dialog-footer {
    width: 100%;
    height: 50px;
    flex-shrink: 0;
    background: #f0f0f0;
    border-top: 1px solid #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-app-region: no-drag;
}

.fullscreen {
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    border-radius: 0 !important;
}
</style>
